import React, { useRef } from "react";

import { trpc } from "@blis/trpc/react";

import { rt, useRealtimeInvalidate } from "@/lib/realtime";
import type { TAccessionDetail, THold, TSample, TTestOrder } from "@/lib/types";
import { AccessionDialogs } from "./AccessionDialogs";
import { AccessionDialogProvider } from "./detail/state";

type AccessionContextValue = {
  accession: TAccessionDetail;
  testOrders: TTestOrder[];
  testOrdersLoading: boolean;
  samples: TSample[];
  samplesLoading: boolean;
  holds: THold[];
  scrollRef: React.RefObject<HTMLDivElement | null>;
  counts: {
    comments: number;
    notes: number;
    holds: number;
    uploads: number;
    instrumentResults: number;
  };
};

export const AccessionContext = React.createContext<AccessionContextValue>(
  {} as AccessionContextValue,
);

export function AccessionProvider({
  accession,
  children,
}: {
  accession: TAccessionDetail;
  children: React.ReactNode;
}) {
  const scrollRef = useRef(null);

  const testOrders = trpc.accession.testOrders.useQuery(accession.id);
  const samples = trpc.accession.samples.useQuery(accession.id);
  const holds = trpc.accession.holds.useQuery(accession.id);
  const counts = trpc.accession.counts.useQuery(accession.id);

  useAccessionRealtime(accession);

  return (
    <AccessionContext.Provider
      value={{
        accession,
        testOrders: testOrders.data ? testOrders.data : [],
        testOrdersLoading: testOrders.isPending,
        holds: holds.data || [],
        scrollRef,
        samples: samples.data || [],
        samplesLoading: samples.isPending,
        counts: counts.data || {
          comments: 0,
          notes: 0,
          holds: 0,
          uploads: 0,
          instrumentResults: 0,
        },
      }}
    >
      <AccessionDialogProvider>
        {children}
        <AccessionDialogs />
      </AccessionDialogProvider>
    </AccessionContext.Provider>
  );
}

export function useAccession() {
  const ctx = React.useContext(AccessionContext);
  if (!ctx) {
    throw new Error("useAccession must be used within a AccessionProvider");
  }
  return ctx;
}

function useAccessionRealtime(accession: TAccessionDetail) {
  const utils = trpc.useUtils();
  useRealtimeInvalidate({
    tags: [rt.accession(accession.id)],
    targets: [
      () => {
        utils.accession.get.invalidate({ id: accession.id });
        utils.accession.testOrders.invalidate(accession.id);
        utils.accession.samples.invalidate(accession.id);
        utils.accession.holds.invalidate(accession.id);
        utils.accession.comments.list.invalidate({ accessionId: accession.id });
        utils.accession.counts.invalidate(accession.id);
        utils.accession.resultEntryOrders.invalidate({
          accessionId: accession.id,
        });
      },
    ],
    throttleMs: 1000,
  });
}
